import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { formatDistanceToNow } from "date-fns";
import { SectionHeading as HeadingTitle } from "../misc/Headings.js";
import { ReactComponent as SvgDecoratorBlob1 } from "../../images/svg-decorator-blob-1.svg";
import { ReactComponent as SvgDecoratorBlob2 } from "../../images/svg-decorator-blob-3.svg";
import useGetYoutubeData from "../../data/videos/useYoutubeVideos";
import { PrimaryButton } from "components/misc/Buttons";

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;
const ThreeColumn = tw.div`flex flex-col items-center lg:items-stretch lg:flex-row flex-wrap`;
const Column = tw.div`mt-12 w-full sm:w-1/2 lg:w-1/3 px-4`;

const addWordSpacing = (text) => {
  if (!text || typeof text !== "string") return null;
  return text.split(" ").map((word, idx) => (
    <span key={idx} style={{ wordSpacing: "0.4rem" }}>
      {word}{" "}
    </span>
  ));
};

const HeadingInfoContainer = tw.div`flex flex-col items-center mb-8`;
const HeadingDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  letter-spacing: 0.3px;
  word-spacing: 3px;
  text-align: center;
  margin-bottom: 1.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  color: #666;
`;

const Card = styled.div`
  ${tw`mx-auto max-w-sm bg-white rounded-lg overflow-hidden`}
  border: 2px solid var(--primary-color);
  transition: all 0.3s ease-in-out;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(199, 138, 65, 0.25);
    border-color: #9f6e33;
  }
`;

const VideoContainer = styled.div`
  ${tw`relative w-full`}
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  background: #ffffff;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const Image = styled.div((props) => [
  `background-image: url("${props.imageSrc}");`,
  tw`bg-cover bg-center h-64 w-full`,
]);

const CardContent = tw.div`p-6`;
const Category = tw.div`text-primary-500 font-bold text-sm [font-family: 'CustomFont'] mb-2`;
const Title = tw.h4`leading-relaxed font-bold text-lg [font-family: 'CustomFont'] text-gray-800 mb-3`;
const PublishDate = styled.p`
  ${tw`text-sm text-gray-700 mb-4 [font-family: 'CustomFont']`}
  font-style: normal;
  word-spacing: 0.2rem;
  letter-spacing: 0.3px;
`;
const Link = styled.a`
  ${tw`inline-block text-center mt-6 text-sm font-semibold cursor-pointer transition-all duration-300 [font-family: 'CustomFont']`}
  background: linear-gradient(135deg, var(--primary-color) 0%, #9f6e33 100%);
  color: white;
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(199, 138, 65, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(199, 138, 65, 0.4);
  }
`;

const LoadMoreButton = styled(PrimaryButton)`
  ${tw`mt-4 mx-auto`}
  background: linear-gradient(135deg, #2F5E4E 0%, #1e3d32 100%);
  padding: 0.6rem 2rem;
  font-size: 0.95rem;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(47, 94, 78, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(47, 94, 78, 0.4);
  }
`;

const DecoratorBlob1 = tw(
  SvgDecoratorBlob1,
)`-z-10 absolute bottom-0 right-0 w-48 h-48 transform translate-x-40 -translate-y-8 opacity-25`;
const DecoratorBlob2 = tw(
  SvgDecoratorBlob2,
)`-z-10 absolute top-0 left-0 w-48 h-48 transform -translate-x-32 translate-y-full opacity-25`;

export default function YoutubeVideosSection() {
  // ✅ queryKey fix: no need to pass here, handled inside hook
  const { data, isLoading, isError, error } = useGetYoutubeData();

  // Debugging logs
  console.group("YouTube Videos Debug");
  console.log("Full API Response:", data);
  console.log("Loading State:", isLoading);
  console.log("Error State:", isError, error);
  console.groupEnd();

  if (isLoading) {
    return (
      <Container>
        <Content>
          <p tw="text-center text-lg">Loading videos...</p>
        </Content>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <Content>
          <p tw="text-center text-red-500">
            Failed to load videos: {error?.message || "Unknown error"}
          </p>
        </Content>
      </Container>
    );
  }

  const items = data?.items || [];

  if (items.length === 0) {
    return (
      <Container>
        <Content>
          <p tw="text-center text-lg">No videos available at the moment.</p>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <HeadingInfoContainer>
          <HeadingTitle>Recent Videos</HeadingTitle>
          <HeadingDescription>
            Recent Videos From Jamia Mohammadia Arabia, Rayadurg Youtube Channel
            <br />
            <Link
              target="_blank"
              href="https://youtube.com/jmardg"
              rel="noopener noreferrer"
            >
              {addWordSpacing("Watch Video")}
            </Link>
          </HeadingDescription>
        </HeadingInfoContainer>
        <ThreeColumn>
          {items.map((post, index) => {
            const videoId = post?.videoId || "";
            const title = post?.title || "Untitled Video";
            const category = post?.category || "Jamia Video";
            const fullUrl =
              post?.fullUrl || `https://www.youtube.com/watch?v=${videoId}`;
            const publishedAt = post?.publishedAt
              ? new Date(post.publishedAt)
              : new Date();
            const relativeTime = formatDistanceToNow(publishedAt, {
              addSuffix: true,
            });

            return (
              <Column key={index}>
                <Card>
                  {videoId ? (
                    <VideoContainer>
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </VideoContainer>
                  ) : (
                    <Image imageSrc="/images/video-placeholder.jpg" />
                  )}
                  <CardContent>
                    <Category>{category}</Category>
                    <Title>{title}</Title>
                    <PublishDate>{relativeTime}</PublishDate>
                    <Link
                      target="_blank"
                      href={fullUrl}
                      rel="noopener noreferrer"
                    >
                      {addWordSpacing("Watch Video")}
                    </Link>
                  </CardContent>
                </Card>
              </Column>
            );
          })}
        </ThreeColumn>
      </Content>
      <DecoratorBlob1 />
      <DecoratorBlob2 />
    </Container>
  );
}
